/**
 * patient-portal data layer — the single place that names the real `patient-portal` endpoints
 * (server/src/modules/patient-portal/routes.js, mounted under /api/v1) and their capability keys.
 * Every screen reads through these hooks so the wire contract lives in one file. Server data lives
 * ONLY in TanStack Query (lab-scoped keys).
 *
 * CLOUD-ONLY (D-22): on a local install the module is not mounted, so each /portal/* path 404s. The
 * publish-gate is a pure default-closed boolean (never 404s for an unknown patient when mounted), so
 * a 404 on it is the definitive "module absent on this install" signal — surfaced via `isModuleAbsent`.
 */
import { qk, useApiQuery } from "@/api";
import type { ApiError } from "@/api";
import { useAuthStore } from "@/auth";
import type { QueryKey } from "@tanstack/react-query";
import type { PublishGateResponse, ResultViewResponse } from "./types";
import { PP_CAPS } from "./caps";

export { PP_CAPS };

/** The module-level gate: empty so each screen gates on its own read capability (see index.tsx). */
export const MODULE_CAPS = [] as const;

// ── Query-key helpers (lab-scoped) ──────────────────────────────────────────────
export function useLabId(): string | null {
  return useAuthStore((s) => s.user?.laboratoireId ?? null);
}

const keys = {
  publishGate: (labId: string | null, patientId: string): QueryKey =>
    qk.scoped(labId, "patient-portal", "publish-gate", patientId),
  resultView: (labId: string | null, demandeId: string): QueryKey =>
    qk.scoped(labId, "patient-portal", "resultats", demandeId),
};

/**
 * A 404 on the publish-gate route = the cloud module is not mounted on this install. The publish-gate
 * is a pure default-closed boolean and NEVER 404s for an unknown patient when mounted, so for that
 * route any 404 is module-absence.
 */
export function isModuleAbsent(error: ApiError | null | undefined): boolean {
  return !!error && error.httpStatus === 404;
}

/**
 * Disambiguate a 404 on the result-view route. When the module IS mounted, the controller returns a
 * 404 with a domain message ("Aucun compte-rendu émis.") for a demande with no `emis` report — a
 * legitimate DATA empty state. When the module is ABSENT, Express's catch-all returns the generic
 * "Route introuvable". Both share code NOT_FOUND, so we read the message to tell them apart; default
 * (a data 404) is the safe, dominant meaning for this route.
 */
export function isResultViewModuleAbsent(error: ApiError | null | undefined): boolean {
  if (!error || error.httpStatus !== 404) return false;
  return /route introuvable|route not found/i.test(error.message);
}

/**
 * GET /portal/publish-gate/:patientId — the LIVE seam-4 pre-publish gate (canPublishToNovera). Used
 * by BOTH the consent-read screen (consent verdict) and the publish-gate screen (publish decision).
 * `enabled` lets the caller hold the probe until a patient id is entered + the capability is present.
 */
export function usePublishGate(patientId: string, enabled: boolean) {
  const labId = useLabId();
  return useApiQuery<PublishGateResponse>(
    keys.publishGate(labId, patientId || "_"),
    `/portal/publish-gate/${encodeURIComponent(patientId)}`,
    { enabled: enabled && patientId.trim() !== "", staleTime: 0 },
  );
}

/**
 * GET /portal/resultats/:demandeId — the walk-in result-view preview (verbatim CompteRendu snapshot).
 * A 404 means no `emis` report yet for that demande (handled as an empty state by the screen) UNLESS
 * the whole module is absent — the screen disambiguates via a shared availability probe.
 */
export function useResultView(demandeId: string, enabled: boolean) {
  const labId = useLabId();
  return useApiQuery<ResultViewResponse>(
    keys.resultView(labId, demandeId || "_"),
    `/portal/resultats/${encodeURIComponent(demandeId)}`,
    { enabled: enabled && demandeId.trim() !== "", staleTime: 0 },
  );
}
